import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  Input,
  IterableDiffer,
  IterableDiffers,
  OnInit,
} from '@angular/core';
import { Applications, Style } from '@local/client-contracts';
import { UiIconModel } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventsService } from '@shared/services';
import { ApplicationsService } from '@shared/services/applications.service';
import { RouterService } from '@shared/services/router.service';
import { WorkspacesService } from 'src/app/bar/services';
import { HubService } from 'src/app/bar/services/hub.service';
export interface ConnectAppsModel {
  apps: string[];
  viewName: 'people' | 'files' | 'mail' | 'calendar' | 'analytics';
  title: { app: string; launcher: string; step?: string; lineBreakAt?: number };
  subtitle?: { app: string; launcher: string; step?: string; lineBreakAt?: number };
  appsHeader?: { title: string; step?: string; lineBreakAt?: number };
  icon: Style.Icon;
  disableAppsTitle?: string;
  appLinks?: string[];
  launcherConnect?: {
    title: string;
    subtitle: string;
  };
}

@UntilDestroy()
@Component({
  selector: 'connect-apps',
  templateUrl: './connect-apps.component.html',
  styleUrls: ['./connect-apps.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConnectAppsComponent implements OnInit, DoCheck {
  private _model: ConnectAppsModel;
  appsDisplayItems: Applications.DisplayItem[] = [];
  private diff: IterableDiffer<string>;
  imgIcon: UiIconModel;
  isOwnerOrAdmin: boolean = false;
  allAppsDisable: boolean = false;
  addPaddingTop: boolean;

  constructor(
    private cdr: ChangeDetectorRef,
    private applicationService: ApplicationsService,
    private differ: IterableDiffers,
    private eventService: EventsService,
    private hubService: HubService,
    private workspacesService: WorkspacesService,
    private routerService: RouterService
  ) {}
  telemetryLocation: string;

  @Input() set model(value: ConnectAppsModel) {
    this._model = value;
    this.setSrc();
  }

  @Input() canReconnectApp: boolean;

  get model(): ConnectAppsModel {
    return this._model;
  }

  ngOnInit(): void {
    this.diff = this.differ.find(this.model?.apps).create();
    this.loadDisplayItems();
    this.telemetryLocation = `${this.hubService.currentLocation}_empty_state`;
    this.workspacesService.ownerOrAdmin$.pipe(untilDestroyed(this)).subscribe((s) => {
      this.isOwnerOrAdmin = s;
    });
    this.hubService.autoFocus = true;
    this.addPaddingTop = !this.hubService.readOnly && this.hubService.activePage === 'people';
  }

  ngDoCheck(): void {
    const changes = this.diff?.diff(this.model?.apps);
    if (changes) {
      this.loadDisplayItems().then(() => {
        this.cdr.markForCheck();
      });
    }
  }

  async loadDisplayItems() {
    const apps = await this.applicationService.all();
    this.appsDisplayItems = apps.filter((a) => this.model.apps.includes(a.id));
    this.allAppsDisable = this.appsDisplayItems.every((app) => app.state?.workspaceDisabled);
  }

  public setSrc() {
    const baseUrl = `/assets/no-connected-app/${this.model.viewName}`;
    const isPeopleView: boolean = this.model.viewName === 'people';

    this.imgIcon = {
      type: 'img',
      value: {
        lightUrl: `${baseUrl}Light.${isPeopleView ? 'png' : 'svg'}`,
        darkUrl: `${baseUrl}Dark.${isPeopleView ? 'png' : 'svg'}`,
      },
    };
  }

  goToSettings() {
    this.eventService.event('settings.go_to_settings');
    this.routerService.navigateByUrl('/admin/search');
  }

  navigateToAppPage(appItem: Applications.DisplayItem) {
    this.routerService.navigate(['admin', 'sources', appItem.id], {
      queryParams: {
        published: appItem.published,
      },
    });
  }
}
